import type {ReactElement} from 'react';
import {useRef} from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {Button, IconPlacement, ButtonSize, ButtonType} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {PopupMenu} from '@Components/popup-menu';
import {PopupMenuItem} from '@Components/popup-menu/components';
import {ProgressDots} from '@Components/progress-dots/progress-dots';
import {SteppedProgressBar} from '@Components/stepped-progress-bar/stepped-progress-bar';
import {Text, TextSize} from '@Components/text';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {usePopoverAnchor} from '@Hooks/usePopoverAnchor';
import {
  useAccountIdsToPublishTo,
  useNextButtonDisabled,
  useNextButtonIcon,
  useNextButtonText,
  usePublishingStatusForAllAccounts,
  useSocialMediaWizardPost,
  useSocialPostPublishingAction,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {useAreCaptionsBeingAutoGenerated, useHideNextButton, useSocialMediaPublishPhase, useWizardCaptionErrors, useWizardTitleErrors} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {useWizardDraftScheduleActions} from '@Components/social-media-wizard/components/social-media-wizard-nav/social-media-wizard-nav.hooks';
import {getFooterLayoutStep, getFooterProgressSteps, shouldShowScheduleDetails} from '@Libraries/social-media-wizard-step-library';
import {getScheduledDateOfPost, shouldShowDraftAndScheduleOptions} from '@Libraries/social-media-wizard-scheduling-library';
import {SocialMediaPublishPhase} from '@Libraries/social-media-publish-phase.library';
import {isDesignCountWithinPlanLimit} from '@Libraries/social-media-wizard-design-library';
import {didAllAccountsErrorOut} from '@Libraries/social-media-wizard-accounts-library';
import {getPremiumUpsellIconClasses} from '@Libraries/premium-library';
import {isUserPremium, openSocialMediaMultiPostingUpsellDialog} from '@Libraries/user.library';
import {PremiumCrownChip} from '@Components/premium-crown-chip/premium-crown-chip';
import {WizardDateTimePicker} from '@Components/social-media-wizard/components/wizard-date-time-picker/wizard-date-time-picker';
import {WizardScheduleControls} from '@Components/social-media-wizard/components/wizard-schedule-controls/wizard-schedule-controls';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import {SocialMediaTestId} from '@/test-ids/social-media-test-id';
import {WizardMobileOptionsSheet} from './components/wizard-mobile-options-sheet/wizard-mobile-options-sheet';
import {WIZARD_MOBILE_OPTIONS_SHEET_PANEL_ID} from './components/wizard-mobile-options-sheet/wizard-mobile-options-sheet.types';
import {WizardMobileScheduledBanner} from './components/wizard-mobile-scheduled-banner/wizard-mobile-scheduled-banner';
import type {SocialMediaWizardFooterProps} from './social-media-wizard-footer.types';
import {PUBLISH_ANIMATABLE_ICON_CLASS, SOCIAL_MEDIA_WIZARD_FOOTER_CONTAINER_QUERY} from './social-media-wizard-footer.types';
import styles from './social-media-wizard-footer.module.scss';

export function SocialMediaWizardFooter({isSmallScreen, hasReachedReviewStep, onNext, onBack, currentSelectedDesigns}: SocialMediaWizardFooterProps): ReactElement {
  const containerRef = useRef<HTMLElement>(null);
  const params = useContainerQuery(containerRef, SOCIAL_MEDIA_WIZARD_FOOTER_CONTAINER_QUERY);
  const {anchorRef: scheduleAnchorRef} = usePopoverAnchor();

  const wizardStep = useWizardStep();
  const socialPost = useSocialMediaWizardPost();
  const isNextButtonDisabled = useNextButtonDisabled();
  const nextButtonStateText = useNextButtonText();
  const nextButtonIcon = useNextButtonIcon();
  const hideNextButton = useHideNextButton();
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const captionErrors = useWizardCaptionErrors();
  const titleErrors = useWizardTitleErrors();
  const publishingAction = useSocialPostPublishingAction();
  const publishPhase = useSocialMediaPublishPhase();
  const isRequestInFlight = publishPhase === SocialMediaPublishPhase.IN_FLIGHT;
  const footerLayoutStep = getFooterLayoutStep(wizardStep, publishPhase);
  const isPostScheduled = shouldShowScheduleDetails(socialPost, footerLayoutStep);
  const areActionsDisabled = areCaptionsBeingAutoGenerated || isRequestInFlight;
  const isPrimaryActionInFlight = isRequestInFlight && publishingAction !== SocialPostPublishingAction.SAVE;
  const isPrimaryActionDisabled = isNextButtonDisabled || (areActionsDisabled && !isPrimaryActionInFlight);

  const {isDateTimePickerVisible, setIsDateTimePickerVisible, onSaveAsDraft, onSchedule, onScheduleAt, openScheduleModal, onSchedulingDone, onDeleteSchedule} =
    useWizardDraftScheduleActions({
      socialPost,
      accountIdsToPublishTo,
      captionErrors,
      titleErrors,
      isSmallScreen,
    });

  const nextButtonText = nextButtonStateText || window.i18next.t('pmwjs_next');

  const isFinalStep = (): boolean => {
    return footerLayoutStep === SocialMediaWizardStep.PUBLISH;
  };

  const shouldShowPremiumUpselling = (): boolean => {
    if (isUserPremium()) {
      return false;
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      return !isDesignCountWithinPlanLimit(currentSelectedDesigns.length);
    }

    return wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS && accountIdsToPublishTo.length > 1;
  };

  const onPremiumUpsell = (): void => {
    openSocialMediaMultiPostingUpsellDialog();
  };

  const onNextButtonClick = (): void => {
    if (shouldShowPremiumUpselling()) {
      onPremiumUpsell();
      return;
    }

    onNext();
  };

  const getIconSpecificButtonClasses = (): string => {
    if (nextButtonIcon === PUBLISH_ANIMATABLE_ICON_CLASS) {
      return 'publish-icon-hover-animation';
    }

    return '';
  };

  const shouldHideBackButton = (): boolean => {
    return isFinalStep() && !didAllAccountsErrorOut(accountIdsToPublishTo, publishingStatusForAllAccounts);
  };

  const renderBackButton = (): ReactElement => {
    return (
      <Button
        customClasses={shouldHideBackButton() ? '_invisible' : ''}
        type={ButtonType.SECONDARY}
        size={ButtonSize.MEDIUM}
        icon="icon-arrow-left"
        text={window.i18next.t('pmwjs_back')}
        disabled={isRequestInFlight}
        onClick={onBack}
      />
    );
  };

  const renderProgress = (): ReactElement => {
    const progress = getFooterProgressSteps(footerLayoutStep);

    if (isSmallScreen) {
      return <ProgressDots currentStep={progress.currentStep} numDots={progress.numSteps} />;
    }

    return (
      <SteppedProgressBar
        className="spacing-m-b-3"
        currentStep={progress.currentStep}
        numSteps={progress.numSteps}
        labels={progress.stepLabels}
        showCheckIconForCompletedStep={isFinalStep()}
      />
    );
  };

  const renderPopupMenuToggleIcon = (): ReactElement => {
    return (
      <Icon
        id="publish-options-menu-icon"
        className="spacing-m-r-2"
        icon="icon-more-h"
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.SQUARE}
        type={IconType.GHOST}
        isDisabled={areActionsDisabled}
      />
    );
  };

  const renderScheduleDetailsPanelMenu = (): ReactElement | null => {
    if (!isPostScheduled) {
      return null;
    }

    return (
      <WizardScheduleControls
        socialPost={socialPost}
        anchorRef={scheduleAnchorRef}
        isDisabled={areActionsDisabled}
        onOpenDateTimePicker={(): void => {
          setIsDateTimePickerVisible(true);
        }}
      />
    );
  };

  const openMobileOptionsSheet = (): void => {
    openMoreOptionsModal({
      panelId: WIZARD_MOBILE_OPTIONS_SHEET_PANEL_ID,
      headerTitle: window.i18next.t('pmwjs_other_options'),
      isSmallScreenModalBottomSheet: true,
      shouldCenterAlignTitle: true,
      content: <WizardMobileOptionsSheet onSaveAsDraft={onSaveAsDraft} onSchedule={onSchedule} />,
    });
  };

  const renderMobileOptionsMenuIcon = (): ReactElement => {
    return (
      <Icon
        id="publish-options-menu-icon"
        className="spacing-m-r-2"
        icon="icon-more-h"
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.SQUARE}
        type={IconType.SECONDARY}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_other_options')}
        isDisabled={areActionsDisabled}
        onClick={openMobileOptionsSheet}
      />
    );
  };

  const renderDetailsPanelMenu = (): ReactElement => {
    if (isSmallScreen) {
      return renderMobileOptionsMenuIcon();
    }

    const isPAYGUser = !isUserPremium();
    const scheduleIconClasses = isPAYGUser ? getPremiumUpsellIconClasses() : 'icon icon-calendar';

    const renderScheduleCrownChip = (): ReactElement | null => {
      if (!isPAYGUser) {
        return null;
      }

      return <PremiumCrownChip className={styles.scheduleUpsellCrownChip} />;
    };

    if (params.xlarge) {
      return (
        <div className={`_position-relative spacing-m-r-2 ${styles.scheduleButtonContainer}`}>
          <Button
            ref={scheduleAnchorRef}
            data-testid={SocialMediaTestId.WIZARD_SCHEDULE_BUTTON}
            type={ButtonType.SECONDARY}
            size={ButtonSize.MEDIUM}
            onClick={onSchedule}
            iconPlacement={IconPlacement.LEFT}
            icon="icon-calendar"
            text={window.i18next.t('pmwjs_schedule')}
            disabled={areActionsDisabled}
          />
          {renderScheduleCrownChip()}
        </div>
      );
    }

    return (
      <div id="popup-menu" ref={scheduleAnchorRef}>
        <PopupMenu menuToggleButton={renderPopupMenuToggleIcon()} maxWidth={undefined} scrollingContainerSelector={undefined}>
          <PopupMenuItem text={window.i18next.t('pmwjs_save_as_draft')} iconClasses="icon icon-save" clickHandler={onSaveAsDraft} />
          <PopupMenuItem text={window.i18next.t('pmwjs_schedule_for_later')} iconClasses={scheduleIconClasses} clickHandler={onSchedule} />
        </PopupMenu>
      </div>
    );
  };

  const shouldShowMobileOptionsOnAccountsStep = (): boolean => {
    if (!isSmallScreen || !hasReachedReviewStep || footerLayoutStep !== SocialMediaWizardStep.SELECT_ACCOUNTS) {
      return false;
    }

    return isUserPremium() && socialPost !== undefined;
  };

  const renderFooterOptionsMenu = (): ReactElement | null => {
    if (shouldShowMobileOptionsOnAccountsStep()) {
      return renderMobileOptionsMenuIcon();
    }

    if (shouldShowDraftAndScheduleOptions(socialPost, footerLayoutStep)) {
      return renderDetailsPanelMenu();
    }

    return null;
  };

  const renderMultiPostUpsellTooltip = (): ReactElement => {
    return (
      <div className="flex-center">
        <Icon size={IconSize.SIZE_ICON_16} icon="icon-crown" className="_unmargin-left spacing-m-r-1 spacing-m-b-1" />
        <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_upsell_multi_posting')} />
      </div>
    );
  };

  const renderNextButton = (): ReactElement => {
    return (
      <Button
        data-testid={SocialMediaTestId.WIZARD_NEXT_BUTTON}
        customClasses={`${hideNextButton ? '_hidden' : ''} ${getIconSpecificButtonClasses()} -noun`}
        disabled={isPrimaryActionDisabled}
        isLoading={isPrimaryActionInFlight}
        type={ButtonType.PRIMARY}
        size={ButtonSize.MEDIUM}
        icon={nextButtonIcon}
        text={nextButtonText}
        onClick={isPostScheduled ? onSchedulingDone : onNextButtonClick}
        iconPlacement={IconPlacement.RIGHT}
      />
    );
  };

  const renderUpsellNextButton = (): ReactElement => {
    const nextButtonWithCrownChip = (
      <div className="_position-relative">
        {renderNextButton()}
        <PremiumCrownChip />
      </div>
    );

    if (isSmallScreen) {
      return nextButtonWithCrownChip;
    }

    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        delay={{show: 100, hide: 100}}
        overlay={
          <Tooltip className={styles.premiumTooltip} placement="top" id="tooltip-upsell">
            {renderMultiPostUpsellTooltip()}
          </Tooltip>
        }
        defaultShow={undefined}
        flip={undefined}
        onHide={undefined}
        onToggle={undefined}
        placement="top"
        popperConfig={undefined}
        show={undefined}
        target={undefined}
      >
        {nextButtonWithCrownChip}
      </OverlayTrigger>
    );
  };

  const renderPrimaryAction = (): ReactElement => {
    if (shouldShowPremiumUpselling()) {
      return renderUpsellNextButton();
    }

    return renderNextButton();
  };

  const renderActions = (): ReactElement | null => {
    if (isFinalStep()) {
      return null;
    }

    return (
      <div className={`flex-center _fit-height _fit-width ${styles.footerActions}`}>
        {renderFooterOptionsMenu()}
        {renderScheduleDetailsPanelMenu()}
        {renderPrimaryAction()}
      </div>
    );
  };

  const renderScheduledMobileContent = (): ReactElement => {
    return (
      <div className={styles.scheduledMobileContainer}>
        <WizardMobileScheduledBanner socialPost={socialPost} onEdit={openScheduleModal} />
        <nav className={styles.contentContainer}>
          {renderBackButton()}
          {renderProgress()}
          <div className={styles.scheduledMobileActions}>
            {renderMobileOptionsMenuIcon()}
            {renderNextButton()}
          </div>
        </nav>
      </div>
    );
  };

  const getScheduledMobileFooterClass = (): string => {
    return isSmallScreen && isPostScheduled ? styles.scheduledMobileFooter : '';
  };

  const renderFooterContent = (): ReactElement => {
    if (isSmallScreen && isPostScheduled) {
      return renderScheduledMobileContent();
    }

    return (
      <nav className={styles.contentContainer}>
        {renderBackButton()}
        {renderProgress()}
        {renderActions()}
      </nav>
    );
  };

  const renderDateTimePicker = (): ReactElement | null => {
    if (isSmallScreen) {
      return null;
    }

    return (
      <WizardDateTimePicker
        anchorRef={scheduleAnchorRef}
        isOpen={isDateTimePickerVisible}
        scheduledDate={getScheduledDateOfPost(socialPost)}
        isEditingExistingSchedule={isPostScheduled}
        onClose={(): void => {
          setIsDateTimePickerVisible(false);
        }}
        onScheduleAt={onScheduleAt}
        onDeleteSchedule={onDeleteSchedule}
      />
    );
  };

  return (
    <>
      <footer ref={containerRef} className={`${styles.footerContainer} ${getScheduledMobileFooterClass()}`}>
        {renderFooterContent()}
      </footer>
      {renderDateTimePicker()}
    </>
  );
}
